import { ChevronDownIcon, ClockIcon, PaperAirplaneIcon } from '@heroicons/react/20/solid';
import React from 'react';
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownHeader,
  DropdownHeading,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection
} from '@oysterjs/ui/dropdown';

import {
  formatSendTimeWithoutZone,
  type ScheduleQuickPick,
  scheduleQuickPicks,
  timeZoneDescription
} from '~/lib/scheduled-send';

/** Calls onUnmount when the menu's items leave the page, after its closing fade. */
const MenuItemsUnmountListener = (props: { onUnmount: () => void }) => {
  const onUnmountRef = React.useRef(props.onUnmount);
  onUnmountRef.current = props.onUnmount;
  React.useEffect(() => () => onUnmountRef.current(), []);
  return null;
};

/**
 * The arrow beside a Send button: Send now again, then the Schedule send choices.
 * A choice runs once the menu has finished closing, since any of them may open a
 * dialog. Opened while the menu was still fading out and handing focus back to the
 * arrow, a dialog flickered as it appeared.
 */
export const ScheduleSendMenu = (props: {
  /** Why the whole menu is off, for a reason the page already shows beside the button. */
  disabledReason?: string;
  /** Turns off only Send now, leaving the schedule choices on. */
  isSendNowDisabled?: boolean;
  /** A line under Send now saying what it does. */
  sendNowDescription?: string;
  onSendNow: () => void;
  onSchedule: (sendAt: Date, quickPick: ScheduleQuickPick['label']) => void;
  onPickDateAndTime: () => void;
  /** Other ways to send, after a divider at the end of the menu. */
  moreChoices?: { label: string; icon: React.ElementType; onSelect: () => void }[];
}) => {
  const now = new Date();
  const isMenuDisabled = !!props.disabledReason;
  const pendingChoiceRef = React.useRef<(() => void) | null>(null);
  const runPendingChoice = () => {
    const pendingChoice = pendingChoiceRef.current;
    pendingChoiceRef.current = null;
    pendingChoice?.();
  };
  // Reopening the menu drops a choice that hasn't run yet.
  const forgetPendingChoice = () => {
    pendingChoiceRef.current = null;
  };

  return (
    <Dropdown>
      <DropdownButton
        color="sky"
        aria-label="More send options"
        title={isMenuDisabled ? props.disabledReason : 'More send options'}
        disabled={isMenuDisabled}
        onClick={forgetPendingChoice}
        className="rounded-l-none border-l border-l-sky-300/60 dark:border-l-sky-300/40 px-2"
      >
        <ChevronDownIcon className="size-4" />
      </DropdownButton>
      {/* Portaled to the body, so only a z-index lifts it over the editor's
          sticky toolbar it opens onto. */}
      <DropdownMenu anchor="bottom end" className="z-50 w-80">
        <MenuItemsUnmountListener onUnmount={runPendingChoice} />
        <DropdownItem
          disabled={isMenuDisabled || props.isSendNowDisabled}
          onClick={() => {
            pendingChoiceRef.current = props.onSendNow;
          }}
        >
          <PaperAirplaneIcon data-slot="icon" />
          <DropdownLabel>Send now</DropdownLabel>
          {props.sendNowDescription && (
            <DropdownDescription>{props.sendNowDescription}</DropdownDescription>
          )}
        </DropdownItem>
        <DropdownDivider />
        <DropdownSection>
          <DropdownHeading>Schedule send</DropdownHeading>
          {scheduleQuickPicks(now).map((quickPick) => (
            <DropdownItem
              key={quickPick.label}
              disabled={isMenuDisabled}
              onClick={() => {
                pendingChoiceRef.current = () =>
                  props.onSchedule(quickPick.sendAt, quickPick.label);
              }}
            >
              <DropdownLabel>{quickPick.label}</DropdownLabel>
              <DropdownDescription>
                {formatSendTimeWithoutZone(quickPick.sendAt)}
              </DropdownDescription>
            </DropdownItem>
          ))}
          <DropdownItem
            disabled={isMenuDisabled}
            onClick={() => {
              pendingChoiceRef.current = props.onPickDateAndTime;
            }}
          >
            <ClockIcon data-slot="icon" />
            <DropdownLabel>Pick a date and time…</DropdownLabel>
          </DropdownItem>
        </DropdownSection>
        <DropdownDivider />
        <DropdownHeader className="text-xs text-neutral-500 dark:text-neutral-400">
          Times are {timeZoneDescription(now)}, your time zone.
        </DropdownHeader>
        {props.moreChoices?.length ? <DropdownDivider /> : null}
        {props.moreChoices?.map((choice) => (
          <DropdownItem
            key={choice.label}
            disabled={isMenuDisabled}
            onClick={() => {
              pendingChoiceRef.current = choice.onSelect;
            }}
          >
            <choice.icon data-slot="icon" />
            <DropdownLabel>{choice.label}</DropdownLabel>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};
